import { useApi } from '@tm/client-app/src/api'
import { useI18n } from '@tm/client-app/src/i18n'
import { Toast } from '@tm/ui/atomic/Toast'
import { Achievement, Goal, Organization } from '@tm/types/db'
import { OrganizationGoals } from '@tm/types/org-goal-tracking'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../auth'

/** The routes that put the goals themselves on screen, where a stale count would be visible. */
const GOAL_ROUTES = ['/', '/getting-started']

export const OrgGoalTrackingContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [goalsLoading, setGoalsLoading] = useState(false)
  const [goals, setGoals] = useState<OrganizationGoals>({ achievements: [], nextGoal: null })
  const [error, setError] = useState<string>()
  const [writeFailed, setWriteFailed] = useState(false)
  const { appApi } = useApi()
  const { t, hasTranslation } = useI18n()
  const { loggedIn, organization } = useAuth()
  const location = useLocation()
  const fetchedForOrganization = useRef<Organization['id'] | null>(null)

  useEffect(() => {
    if (!loggedIn) return

    // The getting-started signature needs the counts on every view, so the first load happens
    // wherever the user enters the app — a deep link or a refresh on a sub-route included.
    // After that only the goal routes refetch: GET /goal-tracking/achievements re-evaluates every
    // goal and writes achievement rows, so it must not run on each navigation. Elsewhere a stale
    // count is invisible, because a goal completing from new data removes the empty state that
    // would have shown it.
    if (fetchedForOrganization.current === organization.id && !GOAL_ROUTES.includes(location.pathname)) return
    fetchedForOrganization.current = organization.id

    setGoalsLoading(true)
    const abortController = new AbortController()
    let settled = false
    appApi
      .get<OrganizationGoals>(
        `/goal-tracking/achievements/${organization.id}`,
        undefined,
        undefined,
        abortController.signal
      )
      .then(response => {
        settled = true
        setGoals(sortByGoalNumber(response.data))
      })
      .catch((err: Error) => {
        if (abortController.signal.aborted) return
        settled = true
        setError(err.message)
      })
      .finally(() => {
        if (!abortController.signal.aborted) setGoalsLoading(false)
      })
    return () => {
      abortController.abort()
      // A load the route change cancelled never delivered its counts. React runs this cleanup
      // before the next effect body, so clearing the marker here lets that run retry. Leaving it
      // set stranded setupProgress at 0/0 for the rest of the session — one navigation during the
      // first request and the getting-started signature and the nav badge silently never appeared.
      if (!settled) fetchedForOrganization.current = null
    }
  }, [appApi, loggedIn, organization.id, location.pathname])

  /** Re-reads the goals without the once-per-organization guard the initial load carries. */
  const reloadGoals = useCallback(async () => {
    try {
      const response = await appApi.get<OrganizationGoals>(`/goal-tracking/achievements/${organization.id}`)
      setGoals(sortByGoalNumber(response.data))
    } catch (err) {
      setError((err as Error).message)
    }
  }, [appApi, organization.id])

  const setAchievementStatus = useCallback(
    (organizationId: Organization['id'], goalId: Goal['id'], status: Achievement['status']) => {
      setGoalsLoading(true)
      appApi
        .put<OrganizationGoals>(
          `/goal-tracking/achievements/${organizationId}/${goalId}`,
          { status },
          undefined,
          undefined
        )
        .then(response => {
          setGoals(sortByGoalNumber(response.data))
        })
        .catch((err: Error) => {
          setError(err.message)
          // The write may well have landed — this route updates the row before it notifies Make,
          // so a failure downstream of that still leaves the server ahead of what is on screen.
          // Re-read rather than guess, and tell the user something went wrong either way: without
          // this, a failed skip looked exactly like a click that did nothing.
          setWriteFailed(true)
          void reloadGoals()
        })
        .finally(() => {
          setGoalsLoading(false)
        })
    },
    [appApi, reloadGoals]
  )

  const skipGoal = useCallback(
    (organizationId: Organization['id'], goalId?: Goal['id']) => {
      if (!goalId) return
      setAchievementStatus(organizationId, goalId, 'skipped')
    },
    [setAchievementStatus]
  )

  const unskipGoal = useCallback(
    (organizationId: Organization['id'], goalId?: Goal['id']) => {
      if (!goalId) return
      setAchievementStatus(organizationId, goalId, 'not_done')
    },
    [setAchievementStatus]
  )

  const value = useMemo(
    () => ({
      isLoading: goalsLoading,
      achievements: goals.achievements,
      error,
      nextGoal: goals.nextGoal,
      setupProgress: countSetupProgress(goals.achievements),
      skipGoal,
      unskipGoal,
    }),
    [goalsLoading, goals.achievements, goals.nextGoal, error, skipGoal, unskipGoal]
  )

  return (
    <OrgGoalTrackingContext.Provider value={value}>
      {children}
      <Toast
        open={writeFailed}
        onClose={() => setWriteFailed(false)}
        message={
          hasTranslation('gettingStarted.updateFailed')
            ? t('gettingStarted.updateFailed')
            : 'Could not update that step just now. Reloaded it — try again.'
        }
      />
    </OrgGoalTrackingContext.Provider>
  )
}

export type SetupProgress = { done: number; total: number }

/**
 * Setup progress as the whole app reads it: the getting-started signature, and the nav badge's
 * count of what is left. A skipped goal counts as settled — the user decided it does not apply to
 * them, so leaving it "open" would mean the badge never clears.
 *
 * `total` comes from the response rather than a constant, so adding or retiring a goal takes no
 * client change.
 */
export function countSetupProgress(achievements: OrganizationGoals['achievements']): SetupProgress {
  return {
    done: achievements.filter(({ status }) => status === 'done' || status === 'skipped').length,
    total: achievements.length,
  }
}

function sortByGoalNumber(data: OrganizationGoals) {
  return {
    achievements: data.achievements.sort((a, b) => a.order - b.order),
    nextGoal: data.nextGoal,
  }
}

export type OrgGoalTracking = OrganizationGoals & {
  isLoading: boolean
  error: string | undefined
  nextGoal: string | null
  setupProgress: SetupProgress
  skipGoal: (organizationId: Organization['id'], goalId?: Goal['id']) => void
  unskipGoal: (organizationId: Organization['id'], goalId?: Goal['id']) => void
}

/**
 * A real default rather than an empty cast: the getting-started signature is read from views that
 * are also rendered outside this provider (tests, and any subtree mounted below the app shell), and
 * an empty object made every one of those a crash on first property access. "No goals known" is the
 * honest answer there, and it renders as no signature at all.
 */
const OrgGoalTrackingContext = createContext<OrgGoalTracking>({
  isLoading: false,
  achievements: [],
  error: undefined,
  nextGoal: null,
  setupProgress: { done: 0, total: 0 },
  skipGoal: () => undefined,
  unskipGoal: () => undefined,
})

export const useOrgGoalTracking = () => useContext(OrgGoalTrackingContext)
